import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  previewBroadcast,
  previewBroadcastUsers,
  getSegmentCounts,
  searchUsers,
  createBroadcast,
  getBroadcasts,
  createScheduledBroadcast,
  getScheduledBroadcasts,
  updateScheduledBroadcast,
  cancelScheduledBroadcast,
  BroadcastFilters,
  CreateBroadcastInput,
  CreateScheduledBroadcastInput,
  ScheduledBroadcastStatus,
} from "../services/broadcast";

/** Count + a page of matched users; fetched on demand (dialog open). */
export function useBroadcastPreviewUsers(
  filters: BroadcastFilters,
  page: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["broadcast-preview-users", filters, page],
    queryFn: () => previewBroadcastUsers(filters, page),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

/** Per-lifecycle-segment counts for the audience tiles. */
export function useSegmentCounts(filters: BroadcastFilters) {
  return useQuery({
    queryKey: ["broadcast-segment-counts", filters],
    queryFn: () => getSegmentCounts(filters),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

/** Searchable user lookup for the hand-pick dropdown. */
export function useUserSearch(query: string) {
  return useQuery({
    queryKey: ["broadcast-user-search", query],
    queryFn: () => searchUsers(query),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** Matched-user count for the current audience filters. */
export function useBroadcastPreview(filters: BroadcastFilters) {
  return useQuery({
    queryKey: ["broadcast-preview", filters],
    queryFn: () => previewBroadcast(filters),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useSendBroadcast() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateBroadcastInput) => createBroadcast(input),
    onSuccess: (res) => {
      const notReg = res.invalidAliases
        ? ` ${res.invalidAliases.toLocaleString()} had no registered device.`
        : "";
      toast({
        title: "Broadcast sent",
        description: `Delivered to ${res.recipients.toLocaleString()} device(s) of ${res.matchedUsers.toLocaleString()} matched.${notReg}`,
      });
      queryClient.invalidateQueries({ queryKey: ["broadcast-history"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to send broadcast",
        variant: "destructive",
      });
    },
  });
}

export function useBroadcastHistory() {
  return useQuery({
    queryKey: ["broadcast-history"],
    queryFn: getBroadcasts,
    refetchOnWindowFocus: false,
  });
}

export function useScheduledBroadcasts() {
  return useQuery({
    queryKey: ["scheduled-broadcasts"],
    queryFn: getScheduledBroadcasts,
    refetchOnWindowFocus: false,
  });
}

export function useCreateScheduledBroadcast() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateScheduledBroadcastInput) =>
      createScheduledBroadcast(input),
    onSuccess: () => {
      toast({
        title: "Broadcast scheduled",
        description: "It will send at the configured time.",
      });
      queryClient.invalidateQueries({ queryKey: ["scheduled-broadcasts"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to schedule broadcast",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateScheduledBroadcast() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: ScheduledBroadcastStatus;
    }) => updateScheduledBroadcast(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-broadcasts"] });
      toast({ title: "Schedule updated" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update schedule",
        variant: "destructive",
      });
    },
  });
}

export function useCancelScheduledBroadcast() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cancelScheduledBroadcast(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-broadcasts"] });
      toast({ title: "Schedule cancelled" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to cancel schedule",
        variant: "destructive",
      });
    },
  });
}
